import type { LocalDatabase } from '../core/types';
import type { BodyMeasurementRecord } from '~/powersync/generated/schema';
import type { CreateBodyMeasurementInput } from './types';

export const BodyMeasurementMutations = {
  /**
   * Insert a body measurement, idempotent on the primary key: a re-import of the
   * same reading carries the same deterministic `id`, so a row that already
   * exists is left untouched — mirroring the server's `ON CONFLICT(id)` upsert.
   *
   * This is a read-then-insert rather than an `INSERT … ON CONFLICT`, because
   * PowerSync exposes every synced table as a SQLite **view** (backed by
   * `ps_data__*` + INSTEAD-OF triggers), and SQLite rejects UPSERT on a view
   * (`cannot UPSERT a view`). A plain `SELECT` + plain `INSERT` are both
   * trigger-supported; wrapping them in one write transaction keeps the
   * existence check and the insert atomic, so two overlapping imports of the
   * same id can't both slip past the check. Manual entries use a random id and
   * never collide — this only matters for the deterministic-id import path.
   */
  create: async (
    db: LocalDatabase,
    input: CreateBodyMeasurementInput,
  ): Promise<BodyMeasurementRecord> => {
    const now = new Date().toISOString();

    const record: BodyMeasurementRecord = {
      ...input,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    return db.transaction().execute(async (trx) => {
      const existing = await trx
        .selectFrom('BodyMeasurement')
        .select('id')
        .where('id', '=', record.id)
        .executeTakeFirst();

      if (existing) return record; // already imported — no-op

      await trx.insertInto('BodyMeasurement').values(record).execute();
      return record;
    });
  },
};
